// SourceReadStatus — one line saying how far reading a source in has come
// (FR-3.3, FR-3.4, FR-3.6): reading its scanned pages (OCR), or failed with
// the reason in plain words. A readable source shows nothing (the resting row
// is the confirmation), and so does a status the server did not send. Pure:
// the status is projected in (`app/sourceReadStatus.ts`).

import { plural } from "@lingui/core/macro";
import { useLingui } from "@lingui/react/macro";
import type { ReactNode } from "react";
import type { SourceReadStatus as Status } from "../../app/sourceReadStatus.js";
import { Icon } from "../Icon/Icon.js";
import { Text } from "../Text/Text.js";
import "./SourceReadStatus.css";

export const SourceReadStatus = ({
    status,
}: {
    readonly status: Status | undefined;
}): ReactNode => {
    const { t } = useLingui();
    if (status === undefined) return null;
    switch (status.type) {
        case "source_read_ready":
            return null;
        case "source_reading_scanned_pages": {
            const pageCount = status.pageCount;
            return (
                <span
                    className="bilda-source-read-status"
                    data-status="reading"
                >
                    <span
                        className="bilda-source-read-status__pulse"
                        aria-hidden="true"
                    />
                    <Text as="span" size="xs" tone="dim">
                        {t`${plural(pageCount, {
                            one: "Reading # scanned page (OCR)…",
                            other: "Reading # scanned pages (OCR)…",
                        })}`}
                    </Text>
                </span>
            );
        }
        case "source_read_failed": {
            const reason = (() => {
                switch (status.reason) {
                    case "original_file_unavailable":
                        return t`Could not be read: the original file could not be loaded. Upload it again.`;
                    case "pages_not_renderable":
                        return t`Could not be read: the scanned pages could not be opened. Try uploading the file again.`;
                    case "recognition_unavailable":
                        return t`Could not be read: text recognition did not answer. Ask about the source in the chat to try again.`;
                }
            })();
            return (
                <span className="bilda-source-read-status" data-status="failed">
                    <Icon name="warning" size="xs" />
                    <Text as="span" size="xs" tone="danger">
                        {reason}
                    </Text>
                </span>
            );
        }
    }
};
